// ─────────────────────────────────────────────────────────────
// SPECIAL ED READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as Martial Arts Kids Class Ready (modeled on Managers), with a NEW palette (royal blue sections,
// sunflower-yellow buttons with navy text, raspberry labels, mint checks), Lexend titles, NEW button copy and a NEW
// section ("Same story, 3 levels": one question shown at Level 1, 2 and 3), to test what converts.
// Promise (must match ads, checkout and the PDF): 52-week plan + the first 10 weeks complete (adapted reading +
// functional math + a life skill every week), $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Classroom material only: no therapy, no "cure", no progress promises, no diagnosis labels, no testimonials.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://special-ed-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Special Ed Ready™ | Your Whole Year of Special Ed Lessons, Already Planned';
export const META_DESCRIPTION =
  'A 52-week plan plus the first 10 weeks complete: adapted stories with picture supports, picture-choice questions and functional math, with 3 support levels. Open this week’s page and teach it on Monday.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'SPECIAL ED READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'GIVE ME MY SUNDAYS BACK';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'MY YEAR, PLANNED · $27';
export const topBarMobileLabel = '52 WEEKS · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR SPECIAL EDUCATION TEACHERS · SELF-CONTAINED, RESOURCE & LIFE SKILLS',
  titleLead: 'YOUR WHOLE YEAR OF SPECIAL ED LESSONS,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 52-week plan plus the first 10 weeks complete: adapted reading and functional math for every level in your room. Open this week’s page and teach it on Monday.',
  mockupAlt: 'Special Ed Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE WEEKS' },
  { value: '30', label: 'MINUTES A DAY' },
  { value: '3', label: 'SUPPORT LEVELS' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'SUNDAY NIGHT VS. MONDAY MORNING.',
  before: {
    label: 'BEFORE',
    when: 'Sunday night',
    quote: '“How do I adapt this for every student?”',
    items: ['Rewriting the gen-ed lesson by hand.', 'Cutting and laminating until midnight.', 'Too hard for some, too easy for others.'],
  },
  after: {
    label: 'AFTER',
    when: 'Monday morning',
    items: ['Open this week’s page.', 'Prep in 5 minutes.', 'Read the story with picture supports.', 'Every student answers at their level.'],
  },
};

// ── 3. WHAT DOES YOUR CLASS NEED THIS WEEK (pick a need, see the week that is ready for it) ──
const block = 'Daily lesson block: greeting & schedule 5 · key words 5 · read together 10 · answer & do 5 · closing & praise 5';
export const selector = {
  eyebrow: 'START WHERE YOUR STUDENTS ARE',
  title: 'WHAT DOES YOUR CLASS NEED THIS WEEK?',
  sub: 'Pick a need and see the week that is ready for it.',
  startLabel: 'READY TO TEACH',
  tablistLabel: 'Class needs',
  columns: ['PAGE 1 · WEEK PLAN', 'PAGE 2 · READ IT', 'PAGE 3 · DO IT'],
  note: 'All 10 weeks follow this same 3-page structure. Adapt each one to your students’ IEPs and your school’s policies.',
  items: [
    {
      tab: 'A calm, predictable start',
      n: 'WEEK 01',
      title: 'My School Day',
      goal: 'The student answers 3 of 5 picture-choice questions about the story and points to the next activity on the class schedule.',
      open: ['Week goal, 5 key words with pictures and 5-minute prep', 'Your opening words: “This week we read a story about our school day. Look at the pictures with me.”', block],
      activity: ['A 7-line adapted story with a picture next to every line', '5 questions with 3 picture choices + teacher key', 'Support levels: point & match, choose & say, read & write'],
      close: ['Sequence & retell: first, next, then, last', 'Functional math: counting 1 to 10', 'Closing activity, quick check and a ready home note'],
    },
    {
      tab: 'Hygiene & independence',
      n: 'WEEK 02',
      title: 'I Can Wash My Hands',
      goal: 'The student puts the 5 handwashing steps in order and counts to 20 while scrubbing.',
      open: ['Week goal, 5 key words with pictures and 5-minute prep', 'Your opening words: “This week we learn to wash our hands, step by step.”', block],
      activity: ['A 7-line adapted story with picture supports', '5 questions with 3 picture choices + teacher key', 'Support levels for every student in the room'],
      close: ['Sequence & retell of the 5 steps', 'Functional math: counting to 20', 'Closing activity at the real sink, quick check and home note'],
    },
    {
      tab: 'Money skills',
      n: 'WEEK 04',
      title: 'At the Grocery Store',
      goal: 'The student finds 3 items from a picture shopping list and pays with $1 bills up to $5.',
      open: ['Week goal, 5 key words with pictures and 5-minute prep', 'Your opening words: “We make a list, we find our food, and we pay.”', block],
      activity: ['A 7-line adapted story with picture supports', '5 questions with 3 picture choices + teacher key', 'Support levels for every student in the room'],
      close: ['Sequence & retell: list, cart, find, pay', 'Functional math: $1 bills up to $5', 'Closing activity: a classroom store with play money'],
    },
    {
      tab: 'Telling time',
      n: 'WEEK 06',
      title: 'Riding the Bus',
      goal: 'The student reads time to the hour on a clock and tells 3 bus safety rules.',
      open: ['Week goal, 5 key words with pictures and 5-minute prep', 'Your opening words: “We wait, we sit, and we use a quiet voice.”', block],
      activity: ['A 7-line adapted story with picture supports', '5 questions with 3 picture choices + teacher key', 'Support levels for every student in the room'],
      close: ['Sequence & retell of the bus routine', 'Functional math: time to the hour', 'Closing activity: a classroom bus with chairs in rows'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE ON YOUR TABLE ON MONDAY.',
  sub: 'Open any page and read it at full size: your 52-week plan, a complete week in 3 pages and your teacher tools.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: ['Your 52-week plan (Q1)', 'Week 04 · week plan', 'Week 04 · read it', 'Week 04 · do it', 'Week 10 · shopping for lunch', 'Week planner + IEP data tracker'],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE ROUTINE FOR EVERY WEEK',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST OPEN AND TEACH.',
  text: 'Weeks 1 to 10 are complete and ready to teach. Weeks 11 to 52 are mapped: a theme, 5 key words, a math skill and a life skill, with a story builder to write the week’s story in 5 minutes.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'PREP', text: 'Print the story and the picture cards in 5 minutes.' },
    { title: 'TEACH', text: 'The same 30-minute routine every day, Monday to Friday.' },
    { title: 'CHECK', text: 'Quick check on Friday and a home note for families.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE WEEK',
  features: [
    { icon: 'goal', title: 'One clear week goal', text: 'One observable sentence, plus 5 key words with a picture for each one.' },
    { icon: 'timer', title: 'A 30-minute lesson block', text: 'Greeting & schedule 5 · Key words 5 · Read together 10 · Answer & do 5 · Closing & praise 5.' },
    { icon: 'steps', title: 'An adapted story', text: 'Short first-person sentences, one per line, with a picture next to every line.' },
    { icon: 'questions', title: 'Questions with picture choices', text: '5 questions with 3 picture choices each, a teacher key and 3 support levels.' },
    { icon: 'quote', title: 'Functional math + a life skill', text: 'Counting, money with dollar bills, telling time and the calendar, tied to real life.' },
    { icon: 'action', title: 'Quick check + home note', text: 'A simple IEP data box (✓ / P / –) and a ready-to-send note for families.' },
  ],
} as const;

// ── 5b. SAME STORY, 3 LEVELS (new section in this landing) ──────────────────
export const levels = {
  eyebrow: 'ONE LESSON. EVERY STUDENT IN YOUR ROOM.',
  title: 'SAME STORY. 3 LEVELS. EVERYONE ANSWERS.',
  sub: 'Tap a level to see how the same question from Week 01 works for each student.',
  storyTitle: 'WEEK 01 · MY SCHOOL DAY',
  story: ['I go to school.', 'I ride the bus to school.', 'I say hi to my teacher.', 'I read a book with my class.', 'I eat lunch with my friends.', 'I play outside.', 'Then I go home.'],
  question: 'How do I get to school?',
  tabs: [
    { level: 'LEVEL 1', name: 'POINT & MATCH', text: 'Two big picture choices. The student points, touches or uses their communication board.' },
    { level: 'LEVEL 2', name: 'CHOOSE & SAY', text: 'Three picture choices. The student chooses and says, signs or shows the word.' },
    { level: 'LEVEL 3', name: 'READ & WRITE', text: 'The student reads the story alone and writes one short sentence.' },
  ],
  sentence: 'I ride the',
  sentenceEnd: 'to school.',
  wordBank: ['bus', 'boat', 'plane'],
  footnote: 'Every complete week has a support level card like this one. Adapt it to each student’s IEP.',
};

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ Weeks 1–10 are complete and ready to teach. Weeks 11–52 are mapped with a theme, 5 key words, a math skill and a life skill. ◆ weeks are IEP data checks.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'ME & MY SCHOOL',
      full: [
        { week: '1–10', label: '10 COMPLETE WEEKS', title: 'From My School Day to Shopping for Lunch' },
        { week: '11', label: 'MAPPED', title: 'Fire Drill Day', mapped: true },
        { week: '12', label: 'MAPPED', title: 'My Feelings', mapped: true },
        { week: '13', label: 'IEP DATA CHECK', title: 'Review and take data', review: true },
      ],
      activities: 'Counting, $1 bills, days of the week, time to the hour and the half hour.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'HOME & EVERYDAY LIFE',
      full: [
        { week: '15', label: 'MAPPED', title: 'Getting Dressed', mapped: true },
        { week: '18', label: 'MAPPED', title: 'Setting the Table', mapped: true },
        { week: '21', label: 'MAPPED', title: 'At the Restaurant', mapped: true },
        { week: '26', label: 'IEP DATA CHECK', title: 'Review and take data', review: true },
      ],
      activities: '+ My Family, Brushing My Teeth, Making a Sandwich, Doing the Laundry, At the Post Office and more.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'OUT IN THE COMMUNITY',
      full: [
        { week: '28', label: 'MAPPED', title: 'Crossing the Street', mapped: true },
        { week: '33', label: 'MAPPED', title: 'Going to the Zoo', mapped: true },
        { week: '38', label: 'MAPPED', title: 'Money in My Wallet', mapped: true },
        { week: '39', label: 'IEP DATA CHECK', title: 'Review and take data', review: true },
      ],
      activities: '+ At the Park, At the Pizza Shop, Planting a Garden, Making New Friends, At the Pool and more.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'PUT IT ALL TOGETHER',
      full: [
        { week: '41', label: 'MAPPED', title: 'Cooking Pancakes', mapped: true },
        { week: '43', label: 'MAPPED', title: 'Using a Phone', mapped: true },
        { week: '50', label: 'MAPPED', title: 'Moving Up', mapped: true },
        { week: '52', label: 'IEP DATA CHECK', title: 'Year-end review', review: true },
      ],
      activities: '+ Field Day, Saving Money, My Classroom Job, A Class Picnic, My Year in Pictures and more.',
    },
  ],
  toolsTitle: 'PLUS YOUR TEACHER TOOLS',
  tools: ['When the day gets hard: 8 situations, what to say and do', 'Week planner, story builder + IEP data tracker', 'Teacher field card for your table'],
};

// ── 7. THE 10 COMPLETE WEEKS ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE FIRST 10 WEEKS, COMPLETE',
  title: 'TEN WEEKS. READY TO TEACH.',
  sub: 'Each week is 3 pages: week plan, read it, do it. A story, a math skill and a life skill your students use outside the classroom.',
  weekLabel: 'MATH:',
  items: [
    { n: '01', week: 'counting 1 to 10', title: 'My School Day', goal: 'Life skill: follow my visual schedule.' },
    { n: '02', week: 'counting to 20', title: 'I Can Wash My Hands', goal: 'Life skill: wash my hands in 5 steps.' },
    { n: '03', week: 'counting scoops 1 to 5', title: 'Making a Snack', goal: 'Life skill: follow a picture recipe.' },
    { n: '04', week: '$1 bills up to $5', title: 'At the Grocery Store', goal: 'Life skill: make a picture shopping list.' },
    { n: '05', week: 'days of the week', title: 'The Weather Today', goal: 'Life skill: dress for the weather.' },
    { n: '06', week: 'time to the hour', title: 'Riding the Bus', goal: 'Life skill: bus safety rules.' },
    { n: '07', week: 'time to the half hour', title: 'Community Helpers', goal: 'Life skill: ask a helper for help.' },
    { n: '08', week: 'counting and sharing equally', title: 'A Birthday Party', goal: 'Life skill: wait my turn and say thank you.' },
    { n: '09', week: 'counting 7 days on a calendar', title: 'Going to the Library', goal: 'Life skill: take care of a borrowed book.' },
    { n: '10', week: 'the next-dollar strategy up to $10', title: 'Shopping for Lunch', goal: 'Life skill: order and pay for my lunch.' },
  ],
  cta: 'GET ALL 10 WEEKS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE YOUR CLASSROOM NEEDS.',
  sub: 'Visual schedules, social stories, family messages, a plan for your aide and IEP data sheets, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: 'Visual Routines & Classroom Schedules Kit',
      hook: 'Make every day predictable.',
      title: 'Visual Routines & Classroom Schedules Kit — Ready-to-Print Visual Schedules, First-Then Boards & Routine Cards',
      text: 'Visual schedules for the whole class and for each student, first-then boards, routine cards for arrival, bathroom, snack, centers, transitions and dismissal, a center rotation chart and a one-afternoon setup guide.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: '30 Ready-to-Read Social Stories',
      hook: 'Ready for the moments that are hard.',
      title: '30 Ready-to-Read Social Stories — Bathroom, Waiting My Turn, Fire Drills & More',
      text: '30 short first-person stories: using the bathroom, waiting my turn, fire drills, a substitute teacher, the cafeteria, asking for a break, field trips and more. Each with picture ideas and 2 check-in questions.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Family Communication Pack',
      hook: 'Know exactly what to send home.',
      title: 'Family Communication Pack — Welcome Letter, Daily Notes & Ready-to-Send Messages',
      text: 'A welcome letter, an “all about my child” form, daily and weekly home notes with picture check boxes, messages for good news and hard days, an IEP meeting invitation and calm scripts for difficult talks.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'The Paraprofessional Playbook',
      hook: 'Every adult in the room knows the plan.',
      title: 'The Paraprofessional Playbook — Work Smoothly with Your Classroom Aide',
      text: 'A first-week meeting agenda, a roles chart, a daily schedule for each adult in the room, station and duty cards, a sub and para binder checklist, quick feedback scripts and a 10-minute weekly check-in.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'IEP Goal Tracking Sheets',
      hook: 'Track IEP goals without the paperwork pile.',
      title: 'IEP Goal Tracking Sheets — Data Sheets with Sample Goals Already Written',
      text: 'Trial-by-trial, frequency and prompt-level data sheets, a weekly progress graph, a bank of sample goals already written (adapt them with your IEP team), progress report sentence starters and a student snapshot.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT MONDAY STARTS HERE',
  title: 'YOUR WHOLE SPECIAL ED YEAR. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'Special Ed Ready™',
  description: 'A 52-week plan with the whole year mapped and the first 10 weeks complete and ready to teach, plus five bonuses: visual routines, social stories, family messages, a paraprofessional playbook and IEP data sheets.',
  mockupAlt: 'Everything included in Special Ed Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    '52-week plan: the whole year mapped',
    'The first 10 weeks complete, 3 pages each',
    'A 30-minute daily lesson block',
    'Adapted stories with picture supports',
    'Picture-choice questions + 3 support levels',
    'Functional math and a life skill every week',
    'Teacher tools: hard moments, planner, IEP tracker',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Special Ed Ready™ (52-week plan + 10 complete weeks)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'YES! SEND ME SPECIAL ED READY — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Classroom material: adapt every lesson to your students’ IEPs, their way of communicating and your school’s policies.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Teach your next weeks with it. If it isn't right for your classroom, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE MONDAY.',
  sub: 'Built for special education teachers and the aides who teach with them. A ready-to-teach lesson book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: a 52-week plan (weeks 1–10 complete, weeks 11–52 mapped with a theme, 5 key words, a math skill and a life skill, plus IEP data check weeks) and the first 10 weeks complete in 3 pages each, plus teacher tools and 5 bonuses.',
    },
    {
      q: 'Which classrooms is it for?',
      a: 'Self-contained classrooms, resource rooms and life skills classes, about K–8. It works for mixed-ability groups: every week has 3 support levels, from pointing to a picture to reading and writing a sentence.',
    },
    {
      q: 'What is inside each complete week?',
      a: 'A week plan with the goal, 5 key words with pictures and a 30-minute daily lesson block; an adapted story with picture supports and 5 picture-choice questions; and a sequencing activity, a functional math activity, a closing activity, a quick check and a home note.',
    },
    {
      q: 'What happens after week 10?',
      a: 'Weeks 11 to 52 are mapped in the plan: each one has a theme, 5 key words, a math skill and a life skill. You use the same 5-day routine, and the story builder on the planner page helps you write the week’s story in 5 minutes.',
    },
    {
      q: 'Does it work with my students’ IEPs?',
      a: 'It is classroom material, not therapy and not an IEP. You choose the support level for each student and adapt every lesson to their IEP goals, their way of communicating and your school’s policies. Students who use a communication device or picture board answer with it.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet at the small table, and the stories, the planner and the IEP data tracker are ready to print.',
    },
    {
      q: 'Can I share it with my paraprofessional?',
      a: 'Yes, you can use it with the aides in your own classroom. Sharing it with other teachers or schools, redistributing it or reselling it is not included in this offer.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “HOW DO I ADAPT THIS FOR EVERY STUDENT?”',
  title: 'WALK IN ON MONDAY WITH THE WHOLE WEEK READY.',
  text: 'Open this week’s page. Prep in 5 minutes. Teach a lesson every student in your room can answer.',
  microcopy: '52-week plan + 10 complete weeks + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'SPECIAL ED READY™ · Classroom material for teachers. Not therapy, not medical advice. Always follow your students’ IEPs and your school’s policies.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
